// Common English stop words and filler words to exclude from extraction
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'about', 'as', 'is', 'was', 'are', 'were',
  'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will',
  'would', 'could', 'should', 'may', 'might', 'shall', 'can', 'that', 'this',
  'these', 'those', 'it', 'its', 'my', 'your', 'his', 'her', 'our', 'their',
  'i', 'me', 'we', 'us', 'you', 'he', 'she', 'they', 'them', 'what', 'which',
  'who', 'how', 'when', 'where', 'why', 'not', 'no', 'so', 'if', 'then',
  'than', 'more', 'one', 'into', 'up', 'out', 'some', 'all', 'own', 'same',
]);

// Curated list of meaningful action verbs associated with inspiration and intention
const VERB_LIST = new Set([
  // Create & make
  'create', 'build', 'make', 'design', 'craft', 'write', 'draw', 'paint',
  'sketch', 'illustrate', 'animate', 'sculpt', 'compose', 'code', 'program',
  'develop', 'produce', 'publish', 'launch', 'ship', 'record', 'film',
  'photograph', 'prototype', 'render', 'model', 'print', 'fabricate',
  // Learn & explore
  'learn', 'explore', 'discover', 'research', 'study', 'read', 'investigate',
  'experiment', 'practice', 'train', 'master', 'understand', 'analyze',
  // Share & connect
  'share', 'teach', 'collaborate', 'connect', 'communicate', 'present',
  'speak', 'mentor', 'coach', 'guide', 'lead', 'inspire', 'network',
  'volunteer', 'contribute', 'publish', 'post', 'exhibit',
  // Act & pursue
  'start', 'begin', 'pursue', 'achieve', 'try', 'attempt', 'test', 'apply',
  'implement', 'execute', 'finish', 'complete', 'commit', 'dedicate',
  'embrace', 'adopt', 'challenge', 'push', 'move',
  // Grow & change
  'grow', 'improve', 'change', 'transform', 'simplify', 'organize', 'plan',
  'restructure', 'redesign', 'rethink', 'reframe', 'evolve', 'refine',
  // Help & serve
  'help', 'support', 'serve', 'give', 'protect', 'heal', 'care', 'assist',
  'fix', 'solve', 'repair',
  // Perform & express
  'perform', 'sing', 'dance', 'play', 'express', 'show', 'demonstrate',
  'cook', 'bake', 'run', 'exercise', 'travel', 'visit', 'meet', 'walk',
  // Gather & curate
  'collect', 'gather', 'curate', 'document', 'archive', 'catalog', 'find',
  'source', 'build', 'assemble',
  // Reflect
  'reflect', 'journal', 'meditate', 'review', 'observe', 'notice',
]);

// Curated list of meaningful subjects and themes associated with inspiration
const SUBJECT_LIST = new Set([
  // Arts & creativity
  'art', 'music', 'design', 'photography', 'film', 'writing', 'painting',
  'drawing', 'animation', 'fashion', 'architecture', 'craft', 'poetry',
  'fiction', 'story', 'stories', 'song', 'songs', 'illustration', 'mural',
  'ceramics', 'sculpture', 'theater', 'dance', 'performance',
  // Technology
  'technology', 'software', 'code', 'data', 'internet', 'digital',
  'hardware', 'engineering', 'programming', 'computing', 'ai',
  'automation', 'robotics', 'startup',
  // Nature & environment
  'nature', 'environment', 'ocean', 'mountain', 'forest', 'garden',
  'animals', 'plants', 'earth', 'space', 'water', 'light', 'sky',
  'climate', 'sustainability', 'ecology', 'wilderness',
  // People & relationships
  'family', 'friends', 'community', 'people', 'children', 'relationship',
  'partnership', 'team', 'culture', 'society', 'connection', 'love',
  'mentorship', 'collaboration', 'belonging',
  // Work & business
  'work', 'career', 'business', 'project', 'company', 'leadership',
  'management', 'entrepreneurship', 'product', 'brand', 'marketing',
  'strategy', 'innovation',
  // Health & wellness
  'health', 'fitness', 'food', 'nutrition', 'wellness', 'mindfulness',
  'meditation', 'sleep', 'exercise', 'sport', 'yoga', 'running', 'movement',
  'habit', 'habits', 'ritual', 'rest',
  // Knowledge & learning
  'science', 'history', 'philosophy', 'psychology', 'education', 'learning',
  'research', 'knowledge', 'books', 'reading', 'biology', 'physics',
  'mathematics', 'neuroscience',
  // Personal growth
  'creativity', 'passion', 'purpose', 'motivation', 'goals', 'skills',
  'growth', 'freedom', 'joy', 'peace', 'beauty', 'identity', 'values',
  'meaning', 'intention', 'simplicity',
  // Places & experience
  'city', 'travel', 'adventure', 'journey', 'world', 'neighborhood',
  'market', 'street',
]);

/**
 * Counts word frequency from an array of words, ignoring stop words.
 */
function countFrequency(words) {
  const counts = {};
  for (const word of words) {
    const clean = word.toLowerCase().replace(/[^a-z]/g, '');
    if (clean.length < 2 || STOP_WORDS.has(clean)) continue;
    counts[clean] = (counts[clean] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([word, count]) => ({ word: capitalize(word), count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Extracts verbs from the `compelledTo` field by matching against the verb list.
 */
function extractVerbs(spark) {
  if (spark.tags?.verbs?.length) return spark.tags.verbs;

  const text = spark.compelledTo || spark.response || '';
  if (!text.trim()) return [];

  return text.trim().split(/\s+/).reduce((acc, word) => {
    const clean = word.toLowerCase().replace(/[^a-z]/g, '');
    if (clean && VERB_LIST.has(clean)) acc.push(clean);
    return acc;
  }, []);
}

/**
 * Extracts subjects from the `moment` field by matching against the subject list.
 */
function extractSubjects(spark) {
  if (spark.tags?.subjects?.length) return spark.tags.subjects;

  const text = spark.moment || spark.trigger || '';
  if (!text.trim()) return [];

  return text.trim().split(/\s+/).reduce((acc, word) => {
    const clean = word.toLowerCase().replace(/[^a-z]/g, '');
    if (clean && SUBJECT_LIST.has(clean)) acc.push(clean);
    return acc;
  }, []);
}

/**
 * Aggregates verb and subject patterns from sparks.
 *
 * @param {Array} sparks - Array of spark objects
 * @param {Object} options
 * @param {boolean} options.ledToAction - If true, only include completed sparks
 * @returns {{ verbs: Array<{word, count}>, subjects: Array<{word, count}> }}
 */
export function getWordPatterns(sparks, { ledToAction = false } = {}) {
  const filtered = ledToAction ? sparks.filter(s => s.completed === true) : sparks;

  const allVerbs = filtered.flatMap(extractVerbs);
  const allSubjects = filtered.flatMap(extractSubjects);

  return {
    verbs: countFrequency(allVerbs),
    subjects: countFrequency(allSubjects),
  };
}

/**
 * Returns a Set of all verb and subject words extracted from a single spark.
 * Used for highlighting matched words in spark detail view.
 *
 * @param {Object} spark
 * @returns {Set<string>}
 */
export function getSparkWordSet(spark) {
  if (!spark) return new Set();
  const verbs = extractVerbs(spark);
  const subjects = extractSubjects(spark);
  return new Set([...verbs, ...subjects]);
}
